import { PLAIN_BUILDING } from './building.js';
import { MILLI_PER_FLOOR, TICK_MS } from './constants.js';
import type { LevelDef, SpawnDef } from './level.js';
import { liftOf, traitsFor } from './level.js';
import { onCar } from './rules/cars.js';
import { beginServicing } from './rules/elevator.js';
import { newPowerState } from './rules/powers.js';
import type { Elevator, ElevatorSpec, GameState, InteractionMode, Passenger } from './types.js';
import type { Loadout } from './upgrades.js';
import { applyLoadout, BASE_LOADOUT } from './upgrades.js';

export interface CreateStateOptions {
  readonly mode: InteractionMode;
  /** Only affects generated content and bots; v0.1 levels are fully scripted. */
  readonly seed?: number;
  /**
   * Hardware the player has installed, read in the level's own lift's parts
   * (ADR-0027). Defaults to the stock car the level is balanced against.
   */
  readonly loadout?: Loadout;
}

/**
 * Build the starting state for a level.
 *
 * Passenger ids are positional (`p0`, `p1`, ...) rather than random so that a
 * replay log recorded against a level definition stays valid as long as that
 * definition's spawn list is unchanged.
 */
export function createInitialState(level: LevelDef, options: CreateStateOptions): GameState {
  const loadout = options.loadout ?? BASE_LOADOUT;
  const spec = applyLoadout(level.elevator, loadout, liftOf(level).parts);

  if (level.floors < 2) {
    throw new Error(`Level ${level.id}: needs at least 2 floors, got ${level.floors}`);
  }
  if (spec.startFloor < 0 || spec.startFloor >= level.floors) {
    throw new Error(
      `Level ${level.id}: startFloor ${spec.startFloor} is outside 0..${level.floors - 1}`,
    );
  }

  for (const car of level.extraCars ?? []) {
    if (!Number.isInteger(car.startFloor) || car.startFloor < 0 || car.startFloor >= level.floors) {
      throw new Error(
        `Level ${level.id}: a car starts at floor ${car.startFloor}, outside 0..${level.floors - 1}`,
      );
    }
  }

  const objective = level.objective;
  if (objective.kind === 'deliverAll' && level.spawns.length === 0) {
    throw new Error(`Level ${level.id}: objective is deliverAll but the level spawns nobody`);
  }
  if (
    (objective.kind === 'deliverN' || objective.kind === 'serviceAbove') &&
    objective.count > level.spawns.length
  ) {
    throw new Error(
      `Level ${level.id}: objective needs ${objective.count} deliveries but only ${level.spawns.length} passengers spawn`,
    );
  }
  if (objective.kind === 'survive' && objective.untilMs > level.timeLimitMs) {
    throw new Error(
      `Level ${level.id}: the shift runs to ${objective.untilMs}ms but the level ends at ${level.timeLimitMs}ms, so it can never be survived`,
    );
  }

  const passengers: Passenger[] = [];
  level.spawns.forEach((spawn, index) => {
    passengers.push(passengerFor(level, spec, spawn, index, passengers));
  });

  // A group that cannot fit in the cabin all at once can never board, which
  // would strand the level silently. Catch it at load instead.
  const groupSizes = new Map<string, number>();
  for (const passenger of passengers) {
    if (passenger.groupId === null) continue;
    groupSizes.set(passenger.groupId, (groupSizes.get(passenger.groupId) ?? 0) + passenger.size);
  }
  for (const [groupId, size] of groupSizes) {
    if (size > spec.capacity) {
      throw new Error(
        `Level ${level.id}: group "${groupId}" needs ${size} slots but the cabin holds ${spec.capacity}, so it can never board`,
      );
    }
  }

  const state: GameState = {
    levelId: level.id,
    seed: options.seed ?? 1,
    mode: options.mode,
    tick: 0,
    elapsedMs: 0,
    floorCount: level.floors,
    building: level.building ?? PLAIN_BUILDING,
    spec,
    loadout,
    stars: level.stars,
    timeLimitMs: level.timeLimitMs,
    speedMilliPerTick: Math.max(1, Math.round((MILLI_PER_FLOOR * TICK_MS) / spec.msPerFloor)),
    // A thousandth of the motor's time a floor, times what the rope leaves of it.
    longRunMilliPerTick: Math.max(
      1,
      Math.round(
        (MILLI_PER_FLOOR * TICK_MS * 1000) /
          (spec.msPerFloor * (1000 - spec.longRunSavingPermille)),
      ),
    ),
    elevator: newCar(spec.startFloor),
    extraCars: (level.extraCars ?? []).map((car) => newCar(car.startFloor)),
    passengers,
    score: {
      delivered: 0,
      totalWaitMs: 0,
      totalRideMs: 0,
      distanceMilli: 0,
      doorCycles: 0,
      abandoned: 0,
      couriersOnTime: 0,
      couriersLate: 0,
      vipsServed: 0,
      transfers: 0,
      trips: 0,
      service: 0,
    },
    // Not on a puzzle, which counts trips: a power would change the count (ADR-0055).
    power: newPowerState(level.objective.kind !== 'withinTrips'),
    status: 'playing',
    rngState: options.seed ?? 1,
    endReason: null,
  };

  if (passengers.some((p) => p.state === 'waiting' && p.origin === spec.startFloor)) {
    beginServicing(state);
  }
  for (const car of state.extraCars) {
    const floor = car.positionMilli / MILLI_PER_FLOOR;
    if (passengers.some((p) => p.state === 'waiting' && p.origin === floor)) {
      onCar(state, car, () => beginServicing(state));
    }
  }

  return state;
}

/** A car parked at a floor with its doors shut and nobody aboard. */
function newCar(startFloor: number): Elevator {
  return {
    positionMilli: startFloor * MILLI_PER_FLOOR,
    phase: 'idle',
    doorPhase: 'closed',
    targetFloor: null,
    phaseTimerMs: 0,
    dwellMs: 0,
    riders: [],
    load: 0,
    boardQueue: [],
    priority: [],
    stops: [],
    squeeze: 0,
    closeRequested: false,
    runFromMilli: startFloor * MILLI_PER_FLOOR,
  };
}

/** Deep copy. Use before mutating a snapshot you still need — `step` mutates in place. */
export function cloneState(state: GameState): GameState {
  return {
    ...state,
    elevator: cloneCar(state.elevator),
    extraCars: state.extraCars.map(cloneCar),
    passengers: state.passengers.map((p) => ({ ...p, errands: [...p.errands] })),
    score: { ...state.score },
    power: { ...state.power },
  };
}

function cloneCar(car: Elevator): Elevator {
  return {
    ...car,
    riders: [...car.riders],
    boardQueue: [...car.boardQueue],
    priority: [...car.priority],
    stops: [...car.stops],
  };
}

/** Current floor as a fractional value, for rendering. Never feed this back into state. */
export function floorPosition(state: GameState, car: Elevator = state.elevator): number {
  return car.positionMilli / MILLI_PER_FLOOR;
}

/** The floor the car is exactly at, or null while it is between floors. */
export function currentFloor(state: GameState, car: Elevator = state.elevator): number | null {
  const { positionMilli } = car;
  return positionMilli % MILLI_PER_FLOOR === 0 ? positionMilli / MILLI_PER_FLOOR : null;
}

/**
 * True when the car is stopped at a floor with the doors shut — the moment a new
 * route decision is meaningful. Bots and the input layer both gate on this.
 */
export function isParked(state: GameState, car: Elevator = state.elevator): boolean {
  return car.phase === 'idle' && car.doorPhase === 'closed' && currentFloor(state, car) !== null;
}

export function passengerById(state: GameState, id: string): Passenger | undefined {
  return state.passengers.find((p) => p.id === id);
}

export function waitingAt(state: GameState, floor: number): Passenger[] {
  return state.passengers.filter((p) => p.state === 'waiting' && p.origin === floor);
}

export function riders(state: GameState): Passenger[] {
  return state.passengers.filter((p) => p.state === 'riding');
}

/** One spawn as a passenger, checked against the level it is in. */
/**
 * A postman's round (ADR-0031): the floors to stop at on the way, each once,
 * and never where they start or finish.
 */
function errandsFor(level: LevelDef, spawn: SpawnDef, index: number): number[] {
  const stops = [...new Set(spawn.stops ?? [])].filter(
    (floor) => floor !== spawn.origin && floor !== spawn.destination,
  );
  for (const floor of stops) {
    if (floor < 0 || floor >= level.floors) {
      throw new Error(
        `Level ${level.id}: spawn ${index} stops at floor ${floor}, outside 0..${level.floors - 1}`,
      );
    }
  }
  if (stops.length > 0 && level.building?.interchange) {
    // A round across the sky lobby would be two rounds; nothing asks for one yet.
    throw new Error(
      `Level ${level.id}: spawn ${index} has stops in a building with an interchange`,
    );
  }
  return stops;
}

function passengerFor(
  level: LevelDef,
  spec: ElevatorSpec,
  spawn: SpawnDef,
  index: number,
  earlier: readonly Passenger[],
): Passenger {
  if (spawn.origin === spawn.destination) {
    throw new Error(`Level ${level.id}: spawn ${index} goes from floor ${spawn.origin} to itself`);
  }
  for (const floor of [spawn.origin, spawn.destination]) {
    if (floor < 0 || floor >= level.floors) {
      throw new Error(
        `Level ${level.id}: spawn ${index} references floor ${floor}, outside 0..${level.floors - 1}`,
      );
    }
  }
  const traits = traitsFor(spawn);
  if (traits.size > spec.capacity) {
    throw new Error(
      `Level ${level.id}: spawn ${index} needs ${traits.size} slots but the cabin holds ${spec.capacity}`,
    );
  }
  // A call-out's return (ADR-0029) appears when the one it follows is
  // delivered, and runs on that one's clock.
  const leader = spawn.follows === undefined ? null : earlier[spawn.follows];
  if (spawn.follows !== undefined && !leader) {
    throw new Error(
      `Level ${level.id}: spawn ${index} follows spawn ${spawn.follows}, which does not come before it`,
    );
  }
  const deadlineMs =
    leader && traits.withinMs === null
      ? leader.deadlineMs
      : traits.withinMs === null
        ? traits.deadlineMs
        : spawn.atMs + traits.withinMs;
  const waiting = !leader && spawn.atMs <= 0;
  return {
    ...traits,
    errands: errandsFor(level, spawn, index),
    deadlineMs,
    id: `p${index}`,
    origin: spawn.origin,
    destination: spawn.destination,
    spawnMs: leader ? Number.MAX_SAFE_INTEGER : spawn.atMs,
    follows: leader ? leader.id : null,
    followDelayMs: leader ? spawn.atMs : 0,
    state: waiting ? 'waiting' : 'unspawned',
    standingFloor: spawn.origin,
    waitingSinceMs: spawn.atMs,
    transferred: false,
    boardableFromCycle: 0,
    boardedMs: null,
    deliveredMs: null,
    abandonedMs: null,
    lostReason: null,
  };
}
